import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Icon } from '@/components/ui/icon';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { useClientTranslation } from '@/lib/core/i18n';
import { useCartStore, useLocaleStore } from '@/lib/core/stores';
import { formatAmount } from '@/lib/core/utils';
import { cn } from '@/lib/core';
import { useApp } from '@/lib/core/providers/appProvider';
import { getShopCheckoutHref, useShoppingBagContent } from '@/services/platform/data/shop';
import { getAddresses } from '@/services/platform/data/addresses';
import { ScreenHead } from '@/components/shared/screen/head';
import type { AddressBookDto, PickupPointContentResponseModel } from '@/services/platform/api/model';
import { SpinLoading } from '@/components/shared/loadings/spin';
import { OrderSummaryAccordionContent } from './OrderSummaryAccordionContent';

type Method = 'delivery' | 'pickup';

export function ShopDeliveryScreen() {
  const t = useClientTranslation();
  const navigate = useNavigate();
  const locale = useLocaleStore(state => state.locale);
  const { appSettings } = useApp();
  const { properties: bagCms, pickupPoints } = useShoppingBagContent();

  const items = useCartStore(state => state.items);
  const itemCount = useCartStore(state => state.itemCount);
  const totalAmount = useCartStore(state => state.totalAmount);
  const shipping = useCartStore(state => state.shipping);
  const discount = useCartStore(state => state.discount);
  const currencyCode = useCartStore(state => state.currencyCode);
  const giftMessage = useCartStore(state => state.giftMessage);
  const setDelivery = useCartStore(state => state.setDelivery);

  const [method, setMethod] = useState<Method>('delivery');
  const [addresses, setAddresses] = useState<AddressBookDto[]>([]);
  const [selectedAddressId, setSelectedAddressId] = useState<string | undefined>(undefined);
  const [selectedPickupPointId, setSelectedPickupPointId] = useState<string | undefined>(undefined);
  const [loading, setLoading] = useState(false);
  const [isSubmitting, setSubmitting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      try {
        const data = await getAddresses(locale);
        if (cancelled) return;
        setAddresses(data ?? []);
        const preferred = data?.find(a => a.isPreferredDelivery) ?? data?.[0];
        if (preferred?.id) setSelectedAddressId(preferred.id);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [locale]);

  useEffect(() => {
    if (pickupPoints.length === 1 && !selectedPickupPointId && pickupPoints[0].id) {
      setSelectedPickupPointId(pickupPoints[0].id);
    }
  }, [pickupPoints, selectedPickupPointId]);

  // Groups pickup points by the CMS "location" picker (falling back to the
  // legacy free-text city for points not yet migrated to the picker).
  const pickupGroups = useMemo(() => {
    const groups = new Map<string, { name: string; points: PickupPointContentResponseModel[] }>();
    for (const point of pickupPoints) {
      const location = point.properties?.location?.[0];
      const key = location?.id ?? point.properties?.city?.trim() ?? '—';
      const name = location?.name ?? point.properties?.city?.trim() ?? '—';
      if (!groups.has(key)) groups.set(key, { name, points: [] });
      groups.get(key)!.points.push(point);
    }
    return groups;
  }, [pickupPoints]);

  /*
   * The base every step of this flow hangs off, resolved once because three things below need it:
   * the proceed CTA, the address-row edit link and the "add new address" button. The last two used
   * to build `/{locale}/shop/checkout/address/new` as a literal, which meant renaming the checkout
   * node in Umbraco left them pointing at a URL `getPageMode` could no longer classify back into
   * `PageMode.ShopAddAddress` — a dead end rather than a form.
   */
  const checkoutHref = getShopCheckoutHref(appSettings);
  const addressFormHref = checkoutHref ? `${checkoutHref}/address/new` : undefined;

  const canProceed =
    !!checkoutHref &&
    ((method === 'delivery' && !!selectedAddressId) || (method === 'pickup' && !!selectedPickupPointId));

  const formattedTotal = formatAmount(totalAmount, currencyCode, locale ?? 'en-GB');
  const subtotal = items.reduce((sum, item) => sum + (item.totalPrice ?? 0), 0);

  const selectedAddress =
    method === 'delivery' ? addresses.find(address => address.id === selectedAddressId) : undefined;
  const selectedPickupPoint =
    method === 'pickup' ? pickupPoints.find(point => point.id === selectedPickupPointId) : undefined;

  const deliverTo =
    selectedAddress || selectedPickupPoint
      ? {
          label:
            selectedAddress?.label ??
            selectedPickupPoint?.properties?.displayName ??
            selectedPickupPoint?.name ??
            '',
          detail: selectedAddress
            ? [
                [selectedAddress.firstName, selectedAddress.lastName].filter(Boolean).join(' '),
                selectedAddress.street,
                selectedAddress.postalCode,
                [selectedAddress.city, selectedAddress.country].filter(Boolean).join(' - '),
              ]
                .filter(Boolean)
                .join(', ')
            : [
                selectedPickupPoint?.properties?.addressLine,
                selectedPickupPoint?.properties?.postalCode,
                [selectedPickupPoint?.properties?.city, selectedPickupPoint?.properties?.country]
                  .filter(Boolean)
                  .join(' - '),
              ]
                .filter(Boolean)
                .join(', '),
        }
      : undefined;

  const proceedToCheckout = async () => {
    if (!canProceed || isSubmitting) return;
    if (!checkoutHref) {
      // Unreachable while `canProceed` gates on it; kept so the navigation below never has to
      // fabricate a URL, and so the cause is named if that gate is ever loosened.
      console.warn('ShopDeliveryScreen: no shopCheckout route configured in app settings.');
      return;
    }
    setSubmitting(true);
    try {
      const persisted = await setDelivery(
        method === 'delivery'
          ? { method, addressId: selectedAddressId }
          : { method, pickupPointId: selectedPickupPointId }
      );
      if (!persisted) return;
      navigate(checkoutHref);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="pt-safe-area relative w-full pb-[calc((5.5rem+env(safe-area-inset-bottom))+100px)]">
      <ScreenHead
        title={bagCms?.deliveryTitle || t('App.Shop.DeliveryMethodTitle')}
        onBack={() => navigate(-1)}
        ariaLabel={t('App.Labels.GoBack')}
        className="border-jncquoi-neutral-200 h-15 border-b-[0.5px]"
        rightActions={
          <div className="flex h-11 items-center justify-center gap-1 px-2">
            {itemCount > 0 && (
              <span className="font-pp-neue-montreal text-jncquoi-neutral-1000 text-[12px] leading-none font-semibold">
                {itemCount}
              </span>
            )}
            <Icon
              name="shopping-bag"
              width={24}
              height={24}
              strokeWidth={1.5}
              className="text-jncquoi-neutral-1000"
            />
          </div>
        }
      />

      {!checkoutHref && (
        <div className="bg-jncquoi-neutral-50 flex flex-col gap-1 px-4 py-3" role="alert">
          <p className="font-pp-neue-montreal text-jncquoi-neutral-800 text-xs font-normal tracking-wide">
            {t('App.Shop.CheckoutUnavailable')}
          </p>
        </div>
      )}

      <Accordion type="single" collapsible className="bg-jncquoi-neutral-50">
        <AccordionItem value="summary" className="border-t-0">
          <AccordionTrigger className="px-4">
            {bagCms?.viewOrderSummary || t('App.Shop.ViewOrderSummary')}
          </AccordionTrigger>
          <AccordionContent className="px-4 pb-4">
            <OrderSummaryAccordionContent
              items={items}
              subtotal={subtotal}
              shipping={shipping}
              discount={discount}
              total={totalAmount}
              currencyCode={currencyCode}
              locale={locale ?? 'en-GB'}
              giftMessage={giftMessage}
              deliverTo={deliverTo}
            />
          </AccordionContent>
        </AccordionItem>
      </Accordion>

      <div className="grid grid-cols-2 gap-2 px-4 pt-6">
        <MethodCard
          selected={method === 'delivery'}
          onSelect={() => setMethod('delivery')}
          icon="delivery-truck"
          label={t('App.Shop.DeliveryOption')}
        />
        <MethodCard
          selected={method === 'pickup'}
          onSelect={() => setMethod('pickup')}
          icon="shop"
          label={t('App.Shop.PickUpOption')}
        />
      </div>

      <div className="px-4 pt-6">
        {method === 'delivery' ? (
          loading ? (
            <SpinLoading wrapperClassName="py-10" />
          ) : (
            <>
              <RadioGroup
                value={selectedAddressId}
                onValueChange={setSelectedAddressId}
                className="flex flex-col"
              >
                {addresses.map(address => (
                  <AddressRow
                    key={address.id}
                    address={address}
                    onEdit={
                      addressFormHref
                        ? () => navigate(`${addressFormHref}?id=${encodeURIComponent(address.id ?? '')}`)
                        : undefined
                    }
                  />
                ))}
              </RadioGroup>
              <div className="mt-6">
                <Button
                  variant="secondary-outline"
                  palette="dark"
                  size="md"
                  className="w-full"
                  disabled={!addressFormHref}
                  onClick={() => addressFormHref && navigate(addressFormHref)}
                >
                  {bagCms?.addNewAddressBtn || t('App.Shop.AddNewAddressCta')}
                </Button>
              </div>
            </>
          )
        ) : pickupPoints.length === 0 ? (
          <p className="font-pp-neue-montreal text-jncquoi-neutral-700 py-10 text-center text-[13px]">
            {t('App.Shop.PickUpNotAvailable')}
          </p>
        ) : (
          <>
            <p className="font-pp-neue-montreal text-jncquoi-neutral-800 py-4 text-xs leading-7.25 font-normal uppercase">
              {t('App.Shop.OurStores')}
            </p>
            <RadioGroup
              value={selectedPickupPointId}
              onValueChange={setSelectedPickupPointId}
              className="flex flex-col"
            >
              <Accordion type="multiple" defaultValue={[...pickupGroups.keys()]}>
                {[...pickupGroups.entries()].map(([key, group]) => (
                  <AccordionItem key={key} value={key}>
                    <AccordionTrigger labelClassName="text-xs font-semibold tracking-wide">
                      {group.name}
                    </AccordionTrigger>
                    <AccordionContent className="pb-2">
                      {group.points.map(point => (
                        <PickupPointRow key={point.id} point={point} />
                      ))}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </RadioGroup>
          </>
        )}
      </div>

      {/*
       * Bottom padding clears the floating AppNavBar pill (fixed at
       * `bottom: 1.5rem + safe-area-inset-bottom`, `h-12` tall — see
       * components/layout/navBar/index.tsx) plus a small gap, so the button
       * doesn't render underneath it.
       */}
      <div className="bg-jncquoi-neutral-00 border-jncquoi-neutral-300 fixed right-0 bottom-0 left-0 z-30 border-t-[0.5px] px-4 pt-4 pb-[calc(5.5rem+env(safe-area-inset-bottom))]">
        <div className="flex items-center gap-4">
          <Button
            variant="primary-solid"
            palette="dark"
            size="md"
            className="flex-1"
            disabled={!canProceed || isSubmitting}
            onClick={proceedToCheckout}
          >
            {bagCms?.proceedToCheckoutBtn || t('App.Shop.ProceedToCheckout')}
          </Button>
          <div className="flex flex-col items-end">
            <span className="font-pp-neue-montreal text-jncquoi-neutral-1000 text-[14px] leading-none font-semibold">
              {formattedTotal}
            </span>
            <span className="font-pp-neue-montreal text-jncquoi-neutral-700 mt-1 text-[11px] leading-none">
              {t('App.Shop.TaxesIncludedNote')}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

interface MethodCardProps {
  selected: boolean;
  onSelect: () => void;
  icon: string;
  label: string;
}

function MethodCard({ selected, onSelect, icon, label }: MethodCardProps) {
  return (
    <button
      type="button"
      onClick={onSelect}
      className={cn(
        'flex flex-col items-center justify-center gap-2 py-6 text-[12px] tracking-[0.5px] uppercase transition-colors',
        selected
          ? 'bg-jncquoi-neutral-1000 text-jncquoi-neutral-00'
          : 'bg-jncquoi-neutral-50 text-jncquoi-neutral-1000'
      )}
    >
      <Icon name={icon} width={28} height={28} strokeWidth={1.5} />
      <span className="font-pp-neue-montreal font-semibold">{label}</span>
    </button>
  );
}

interface PickupPointRowProps {
  point: PickupPointContentResponseModel;
}

function PickupPointRow({ point }: PickupPointRowProps) {
  const props = point.properties;
  const cityCountry = [props?.city, props?.country].filter(Boolean).join(' - ');
  return (
    <label
      htmlFor={`pickup-${point.id}`}
      className="border-jncquoi-neutral-200 flex items-start gap-3 border-b-[0.5px] py-4 last:border-b-0"
    >
      <RadioGroupItem id={`pickup-${point.id}`} value={point.id ?? ''} className="mt-1" />
      <div className="min-w-0 flex-1">
        <span className="font-pp-neue-montreal text-jncquoi-neutral-1000 text-sm font-semibold tracking-wide">
          {props?.displayName || point.name || '—'}
        </span>
        <div className="font-pp-neue-montreal text-jncquoi-neutral-1000 mt-2 flex flex-col gap-1 text-sm tracking-wide">
          {props?.addressLine && <span>{props.addressLine}</span>}
          {props?.additionalInfo && <span>{props.additionalInfo}</span>}
          {props?.postalCode && <span>{props.postalCode}</span>}
          {cityCountry && <span>{cityCountry}</span>}
        </div>
        {props?.deliveryLabel && (
          <p className="font-pp-neue-montreal text-jncquoi-neutral-700 mt-2 text-xs tracking-wide">
            {props.deliveryLabel}
          </p>
        )}
      </div>
    </label>
  );
}

interface AddressRowProps {
  address: AddressBookDto;
  /** Omitted when the address form has no resolvable URL; the edit affordance is hidden instead. */
  onEdit?: () => void;
}

function AddressRow({ address, onEdit }: AddressRowProps) {
  const t = useClientTranslation();
  const cityCountry = [address.city, address.country].filter(Boolean).join(' - ');
  return (
    <label
      htmlFor={`address-${address.id}`}
      className="border-jncquoi-neutral-200 flex items-start gap-3 border-b-[0.5px] py-4"
    >
      <RadioGroupItem id={`address-${address.id}`} value={address.id ?? ''} className="mt-1" />
      <div className="min-w-0 flex-1">
        <div className="flex items-center justify-between">
          <span className="font-pp-neue-montreal text-jncquoi-neutral-1000 text-[14px] font-semibold">
            {address.label ?? '—'}
          </span>
          {onEdit && (
            <button
              type="button"
              aria-label={t('App.Labels.EditProfile')}
              className="flex size-6 items-center justify-center"
              onClick={event => {
                event.preventDefault();
                onEdit();
              }}
            >
              <Icon name="edit" width={24} height={24} className="text-jncquoi-neutral-1000" />
            </button>
          )}
        </div>
        <div className="font-pp-neue-montreal text-jncquoi-neutral-1000 mt-2 flex flex-col gap-1 text-[13px] tracking-[0.25px]">
          {address.firstName || address.lastName ? (
            <span>{[address.firstName, address.lastName].filter(Boolean).join(' ')}</span>
          ) : null}
          {address.street && <span>{address.street}</span>}
          {address.postalCode && <span>{address.postalCode}</span>}
          {cityCountry && <span>{cityCountry}</span>}
        </div>
        <div className="font-pp-neue-montreal text-jncquoi-neutral-700 mt-2 flex items-center gap-2 text-[11px] tracking-[0.5px]">
          <span>{t('App.Shop.FreeShipping')}</span>
          <span aria-hidden="true">•</span>
          <span>{t('App.Shop.EstimatedDelivery', { days: t('App.Shop.EstimatedDeliveryDefault') })}</span>
        </div>
      </div>
    </label>
  );
}
